import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { VIDEO_SLIDES } from "@/lib/data";

const SLIDE_MS = 7500;

function useVideoSlides() {
    const [idx, setIdx] = useState(0);
    const videoRef = useRef(null);

    // Auto-advance image slides; vimeo loops in background mode, video advances onEnded
    useEffect(() => {
        if (VIDEO_SLIDES[idx].type !== "image") return;
        const t = setTimeout(() => setIdx((i) => (i + 1) % VIDEO_SLIDES.length), SLIDE_MS);
        return () => clearTimeout(t);
    }, [idx]);

    // (re)load + play whenever a video slide becomes active
    useEffect(() => {
        if (VIDEO_SLIDES[idx].type !== "video") return;
        const v = videoRef.current;
        if (!v) return;
        v.load();
        const p = v.play();
        if (p && typeof p.catch === "function") p.catch(() => {});
    }, [idx]);

    const advance = () => setIdx((i) => (i + 1) % VIDEO_SLIDES.length);

    return { idx, setIdx, videoRef, advance, current: VIDEO_SLIDES[idx] };
}

export default function VideoFeature() {
    const { idx, setIdx, videoRef, advance, current } = useVideoSlides();

    return (
        <section
            data-testid="video-feature"
            className="relative h-[80vh] md:h-screen min-h-[560px] overflow-hidden bg-ink"
        >
            {/* Slider stage */}
            <AnimatePresence mode="sync">
                <motion.div
                    key={idx}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 1.4, ease: "easeOut" }}
                    className="absolute inset-0"
                >
                    <SlideMedia current={current} videoRef={videoRef} onVideoEnded={advance} />
                </motion.div>
            </AnimatePresence>

            {/* Overlay */}
            <div className="absolute inset-0 bg-black/55" />

            {/* Content */}
            <div className="relative z-10 h-full flex items-center">
                <div className="container-wide">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1 }}
                        className="max-w-3xl"
                    >
                        <div className="flex items-center gap-4 mb-6">
                            <span className="divider-rule bg-clay" />
                            <span className="text-xs tracking-[0.3em] uppercase text-bone/80 font-medium">
                                Residential &amp; Commercial
                            </span>
                        </div>

                        <h2 className="font-display text-bone text-3xl md:text-5xl lg:text-6xl font-light leading-[1.1] tracking-tight">
                            Expert advice from{" "}
                            <span className="italic text-clay">design and planning</span> through to completion.
                        </h2>

                        <p className="mt-6 text-bone/70 text-base md:text-lg max-w-xl">
                            Whatever your project, we work closely with you to ensure every detail is perfect — from the first sketch to the final walkthrough.
                        </p>

                        <div className="mt-10">
                            <Link
                                to="/projects?p=north-avoca"
                                data-testid="video-cta-see-more"
                                className="btn-ghost-light"
                            >
                                See More
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </div>

            {/* Slide indicators */}
            {VIDEO_SLIDES.length > 1 && (
                <div className="absolute bottom-6 md:bottom-10 right-6 md:right-12 z-10 flex items-center gap-3">
                    {VIDEO_SLIDES.map((s, i) => (
                        <button
                            key={s.id || s.src}
                            onClick={() => setIdx(i)}
                            aria-label={`Go to slide ${i + 1}`}
                            data-testid={`video-slide-dot-${i}`}
                            className={`h-px transition-all duration-500 ${
                                i === idx ? "w-10 bg-bone" : "w-5 bg-bone/40 hover:bg-bone/70"
                            }`}
                        />
                    ))}
                </div>
            )}
        </section>
    );
}

function SlideMedia({ current, videoRef, onVideoEnded }) {
    if (current.type === "video") {
        return (
            <video
                ref={videoRef}
                src={current.src}
                poster={current.poster}
                autoPlay
                muted
                playsInline
                preload="auto"
                onEnded={onVideoEnded}
                className="w-full h-full object-cover"
            />
        );
    }
    if (current.type === "vimeo") {
        return (
            <div className="absolute inset-0 overflow-hidden">
                <iframe
                    title="brazier-video"
                    src={`https://player.vimeo.com/video/${current.id}?background=1&autoplay=1&loop=1&muted=1&autopause=0`}
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[177.77vh] h-[56.25vw] min-w-full min-h-full pointer-events-none"
                />
            </div>
        );
    }
    return (
        <img
            src={current.src}
            alt=""
            aria-hidden="true"
            className={`w-full h-full object-cover ${
                current.ken ? `kb-${current.ken}` : "kb-zoom-out"
            }`}
        />
    );
}
